/**
 * Esecuzioni — the operator's run log under the "Observe" group. ONE page,
 * TWO tabs (owner's call): **Workflow** (workflow_executions, per-node trace)
 * and **ETL** (etl_runs across every pipeline, ADR-0038). They're DIFFERENT
 * entities (different columns + drill-down + RBAC), so they're co-located as
 * tabs, NOT merged into one bastard grid — same neighbourhood, two rooms.
 *
 * Each tab gates its OWN query on its OWN RBAC (`executions` / `etl`), like the
 * Dashboard plane cards: a scoped operator only sees (and only fetches) the
 * tabs they may read.
 */
import { useQuery } from "@tanstack/react-query";
import { Activity, RefreshCw, Workflow } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ExecutionDetailDrawer } from "@/components/workflows/ExecutionDetailDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { etlApi, type RunStatus } from "@/lib/api-etl";
import {
  workflowExecutionsApi,
  type DeliverySummary,
  type ExecutionSummary,
} from "@/lib/api-workflows";
import { cn } from "@/lib/cn";
import { usePaginationState } from "@/lib/pagination";
import { usePermissions } from "@/lib/permissions";

type StatusFilter = "all" | ExecutionSummary["status"];
type TriggerFilter = "all" | ExecutionSummary["trigger"];

const STATUS_BADGE: Record<ExecutionSummary["status"], { tone: "active" | "danger" | "neutral" | "warning"; label: string }> = {
  succeeded: { tone: "active", label: "ok" },
  failed: { tone: "danger", label: "failed" },
  running: { tone: "warning", label: "running" },
  cancelled: { tone: "neutral", label: "cancelled" },
  waiting: { tone: "warning", label: "in attesa" },
  resuming: { tone: "warning", label: "in ripresa" },
};

const ETL_STATUS_BADGE: Record<RunStatus, { tone: "active" | "danger" | "neutral" | "warning"; label: string }> = {
  succeeded: { tone: "active", label: "ok" },
  failed: { tone: "danger", label: "failed" },
  running: { tone: "warning", label: "running" },
  cancelling: { tone: "warning", label: "cancelling" },
  pending: { tone: "neutral", label: "pending" },
  cancelled: { tone: "neutral", label: "cancelled" },
};

function formatDuration(started: string | null, finished: string | null): string {
  if (!started || !finished) return "—";
  const ms = new Date(finished).getTime() - new Date(started).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

type Tab = "workflow" | "etl";

export function WorkflowRunsPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canWf = can("executions", "read");
  const canEtl = can("etl", "read");

  const [tab, setTab] = React.useState<Tab>("workflow");
  // If the active tab isn't permitted, fall through to the first one that is.
  const visible = (["workflow", "etl"] as Tab[]).filter((x) => (x === "workflow" ? canWf : canEtl));
  const active: Tab = visible.includes(tab) ? tab : (visible[0] ?? "workflow");

  return (
    <PageContainer>
      <PageHeader
        title={t("workflowRuns.title")}
        description={t("workflowRuns.subtitle")}
      />
      <PageContent>
        {visible.length > 1 && (
          <div className="mb-4 inline-flex rounded-md border border-border p-0.5 text-sm">
            <button
              type="button"
              className={cn(
                "inline-flex items-center gap-1.5 rounded px-3 py-1",
                active === "workflow" ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setTab("workflow")}
            >
              <Workflow className="h-3.5 w-3.5" strokeWidth={1.5} /> Workflow
            </button>
            <button
              type="button"
              className={cn(
                "inline-flex items-center gap-1.5 rounded px-3 py-1",
                active === "etl" ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setTab("etl")}
            >
              <Activity className="h-3.5 w-3.5" strokeWidth={1.5} /> ETL
            </button>
          </div>
        )}

        {active === "workflow" ? <WorkflowRunsTab /> : <EtlRunsTab />}
      </PageContent>
    </PageContainer>
  );
}

// --- Workflow tab (the original page body) ---------------------------------

function WorkflowRunsTab() {
  const [status, setStatus] = React.useState<StatusFilter>("all");
  const [trigger, setTrigger] = React.useState<TriggerFilter>("all");
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const { page, pageSize, setPage, setPageSize, reset } = usePaginationState();

  const query = useQuery({
    queryKey: ["workflow-runs", status, trigger, page, pageSize],
    queryFn: () =>
      workflowExecutionsApi.list({
        status: status === "all" ? undefined : status,
        trigger: trigger === "all" ? undefined : trigger,
        page,
        page_size: pageSize,
      }),
    refetchInterval: 5_000,
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const isEmpty = !query.isLoading && rows.length === 0;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Status</span>
          <select
            className="border border-border bg-background px-2 py-1"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter);
              reset();
            }}
          >
            <option value="all">all</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Trigger</span>
          <select
            className="border border-border bg-background px-2 py-1"
            value={trigger}
            onChange={(e) => {
              setTrigger(e.target.value as TriggerFilter);
              reset();
            }}
          >
            <option value="all">all</option>
            <option value="webhook">webhook</option>
            <option value="cron">cron</option>
            <option value="manual">manual</option>
            <option value="event">event</option>
            <option value="replay">replay (test-run)</option>
            <option value="delivery_hook">delivery hook</option>
            <option value="row_changed">row changed</option>
            <option value="form_submitted">form submitted</option>
            <option value="button_clicked">button clicked</option>
            <option value="sub_workflow">sub-workflow</option>
            <option value="workflow_node">workflow node</option>
          </select>
        </div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => query.refetch()}>
          <RefreshCw className={"h-4 w-4 " + (query.isFetching ? "animate-spin" : "")} strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={Activity}
          title="No runs yet"
          description="Once a workflow is triggered (test-run from the editor, webhook, cron, …) it appears here with its full per-node trace."
        />
      ) : (
        <div className="rounded-lg overflow-hidden border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th>Workflow</Th>
                <Th className="w-20">Trigger</Th>
                <Th className="w-24">Status</Th>
                <Th className="w-28">Delivery</Th>
                <Th className="w-16 text-right">Nodes</Th>
                <Th className="w-20 text-right">Duration</Th>
                <Th className="w-44">Started</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((e) => {
                const badge = STATUS_BADGE[e.status];
                return (
                  <Tr
                    key={e.id}
                    onClick={() => setSelectedId(e.id)}
                    className="cursor-pointer hover:bg-accent/5"
                  >
                    <Td>
                      <Link
                        to={`/workflows/${e.workflow_id}`}
                        onClick={(ev) => ev.stopPropagation()}
                        className="font-medium hover:text-accent"
                      >
                        {e.workflow_name}
                      </Link>
                      {e.workflow_version != null && (
                        <span className="ml-2 font-mono text-xs text-muted-foreground">
                          v{e.workflow_version}
                        </span>
                      )}
                    </Td>
                    <Td className="text-xs text-muted-foreground">{e.trigger}</Td>
                    <Td>
                      <div className="inline-flex items-center gap-1.5">
                        <Badge tone={badge.tone}>{badge.label}</Badge>
                        {e.failed_node_count > 0 && (
                          <span
                            className="font-mono text-xs text-danger"
                            title={`${e.failed_node_count} failed node${e.failed_node_count === 1 ? "" : "s"}`}
                          >
                            ×{e.failed_node_count}
                          </span>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <DeliveryBadge summary={e.delivery_summary} />
                    </Td>
                    <Td className="text-right font-mono text-xs">{e.node_count}</Td>
                    <Td className="text-right font-mono text-xs">
                      {formatDuration(e.started_at, e.finished_at)}
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">
                      {new Date(e.started_at).toLocaleString()}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <ExecutionDetailDrawer
        open={!!selectedId}
        onOpenChange={(o) => !o && setSelectedId(null)}
        executionId={selectedId}
        onNavigateExecution={(id) => setSelectedId(id)}
      />
    </>
  );
}

// --- ETL tab (global etl_runs across every pipeline) -----------------------

function EtlRunsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<"all" | RunStatus>("all");
  const { page, pageSize, setPage, setPageSize, reset } = usePaginationState();

  const query = useQuery({
    queryKey: ["etl-runs-global", status, page, pageSize],
    queryFn: () =>
      etlApi.allRuns({
        status: status === "all" ? undefined : status,
        page,
        page_size: pageSize as 25 | 50 | 100,
      }),
    refetchInterval: 5_000,
  });

  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const isEmpty = !query.isLoading && rows.length === 0;

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Status</span>
          <select
            className="border border-border bg-background px-2 py-1"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as "all" | RunStatus);
              reset();
            }}
          >
            <option value="all">all</option>
            <option value="pending">pending</option>
            <option value="running">running</option>
            <option value="succeeded">succeeded</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
          </select>
        </div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => query.refetch()}>
          <RefreshCw className={"h-4 w-4 " + (query.isFetching ? "animate-spin" : "")} strokeWidth={1.5} />
          Refresh
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={Activity}
          title={t("etl.runs.emptyTitle", "Nessun run ETL")}
          description={t(
            "etl.runs.emptyBody",
            "Quando una pipeline ETL viene eseguita (manuale, schedulata o da un workflow) appare qui con righe lette/scritte e scarti.",
          )}
        />
      ) : (
        <div className="rounded-lg overflow-hidden border border-border/60">
          <Table>
            <Thead>
              <Tr>
                <Th>Pipeline</Th>
                <Th className="w-28">Corsia</Th>
                <Th className="w-16 text-right">Priorità</Th>
                <Th className="w-20">Trigger</Th>
                <Th className="w-24">Stato</Th>
                <Th className="w-28 text-right">Righe</Th>
                <Th className="w-16 text-right">Scarti</Th>
                <Th className="w-20 text-right">Durata</Th>
                <Th className="w-44">Avvio</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => {
                const badge = ETL_STATUS_BADGE[r.status];
                return (
                  <Tr
                    key={r.id}
                    onClick={() => navigate(`/etl/${r.pipeline_id}?run=${r.id}`)}
                    className="cursor-pointer hover:bg-accent/5"
                  >
                    <Td>
                      <Link
                        to={`/etl/${r.pipeline_id}?run=${r.id}`}
                        onClick={(ev) => ev.stopPropagation()}
                        className="font-medium hover:text-accent"
                      >
                        {r.pipeline_name || r.pipeline_id.slice(0, 8)}
                      </Link>
                    </Td>
                    <Td className="text-xs text-muted-foreground">
                      {r.lane_name ?? <span className="text-muted-foreground/50">—</span>}
                    </Td>
                    <Td className="text-right font-mono text-xs text-muted-foreground">
                      {r.priority}
                    </Td>
                    <Td className="text-xs text-muted-foreground">{r.trigger}</Td>
                    <Td>
                      <Badge tone={badge.tone}>{badge.label}</Badge>
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {r.rows_in}
                      <span className="text-muted-foreground"> → </span>
                      {r.rows_out}
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {r.dead_lettered > 0 ? (
                        <span className="text-danger" title={`${r.dead_lettered} righe scartate`}>
                          {r.dead_lettered}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </Td>
                    <Td className="text-right font-mono text-xs">
                      {formatDuration(r.started_at, r.finished_at)}
                    </Td>
                    <Td className="font-mono text-xs text-muted-foreground">
                      {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </>
  );
}

/** Compact delivery state badge for the workflow runs table.
 *
 * 0 deliveries → "—"; all succeeded → ✓; pending/in_flight → ⏳; partial
 * dead-letter → ⚠; only dead_letter/failed → ✗.
 */
function DeliveryBadge({ summary }: { summary?: DeliverySummary }) {
  const s = summary ?? {
    pending: 0,
    in_flight: 0,
    succeeded: 0,
    failed: 0,
    dead_letter: 0,
  };
  const total = s.pending + s.in_flight + s.succeeded + s.failed + s.dead_letter;
  if (total === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  const inFlight = s.pending + s.in_flight;
  const terminalBad = s.dead_letter + s.failed;

  if (inFlight > 0) {
    return (
      <Badge tone="neutral" className="gap-1 text-xs">
        ⏳ {inFlight}/{total}
      </Badge>
    );
  }
  if (terminalBad > 0 && s.succeeded > 0) {
    return (
      <Badge tone="warning" className="gap-1 text-xs" title="Partial delivery">
        ⚠ {s.succeeded}/{total}
      </Badge>
    );
  }
  if (terminalBad > 0) {
    return (
      <Badge tone="danger" className="gap-1 text-xs">
        ✗ {terminalBad}
      </Badge>
    );
  }
  return (
    <Badge tone="active" className="gap-1 text-xs">
      ✓ {s.succeeded}
    </Badge>
  );
}
